import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../store';
import { searchPlayers, getPlayerImage } from '../../services/api';
import { Position, Player, ApiPlayer } from '../../types';
import { addPlayer, addNotification } from '../../store/teamSlice';
import { getCountries, getLeaguesByCountry, getTeamsByLeague } from '../../data/soccerData';
import './PlayerSearch.css';

// Define search filter type
interface SearchFilters {
  query: string;
  position: Position | '';
  year: string;
  country: string;
  league: string;
  team: string;
}

// Export the handle type for use in other components
export interface PlayerSearchHandle {
  focusSearchInput: () => void;
}

const PlayerSearch = forwardRef<PlayerSearchHandle, {}>((props, ref) => {
  const dispatch = useDispatch();
  const selectedPosition = useSelector((state: RootState) => state.team.selectedPosition) as Position | null;
  const searchInputRef = useRef<HTMLInputElement>(null);
  
  // Expose the focus method to parent components
  useImperativeHandle(ref, () => ({
    focusSearchInput: () => {
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }
  }));
  
  // Updated search state with all filters
  const [filters, setFilters] = useState<SearchFilters>({
    query: '',
    position: '',
    year: '',
    country: '',
    league: '',
    team: '',
  });
  
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
  const [searchResults, setSearchResults] = useState<ApiPlayer[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [usingLocalData, setUsingLocalData] = useState(false);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Dynamic options based on selections
  const countries = getCountries();
  const availableLeagues = filters.country ? getLeaguesByCountry(filters.country) : [];
  const availableTeams = filters.country && filters.league ? getTeamsByLeague(filters.country, filters.league) : [];

  // Handle filter change
  const handleFilterChange = (key: keyof SearchFilters, value: string) => {
    setFilters(prev => {
      const newFilters = { ...prev, [key]: value };
      
      // Clear dependent filters when parent changes
      if (key === 'country') {
        newFilters.league = '';
        newFilters.team = '';
      } else if (key === 'league') {
        newFilters.team = '';
      }
      
      return newFilters;
    });
    
    // If query or important filters change, trigger search after short delay
    if (key === 'query' && value.length >= 3) {
      triggerDebouncedSearch();
    }
  };
  
  // Debounced search function
  const triggerDebouncedSearch = () => {
    // Clear any existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      handleSearch();
    }, 300); // 300ms delay for debounce
  };
  
  const handleSearch = async () => {
    if (filters.query.trim() === '' && !filters.position && !filters.country && !filters.league && !filters.year && !filters.team) {
      setErrorMessage('Please enter at least one search criteria');
      return;
    }

    setIsSearching(true);
    setErrorMessage('');
    setUsingLocalData(false);
    
    console.log('🚀 Starting enhanced search with filters:', filters);
    
    try {
      // Pass all relevant filters to search function
      const results = await searchPlayers(
        filters.query, 
        filters.position as Position | undefined,
        {
          year: filters.year ? parseInt(filters.year, 10) : undefined,
          country: filters.country || undefined,
          league: filters.league || undefined,
          team: filters.team || undefined
        }
      );
      
      console.log('✅ Search completed, found:', results.length, 'players');
      setSearchResults(results);
      
      // Check if we're using local data
      if (filters.query.trim() && results.length > 0) {
        // This is a heuristic to detect if we're using local data
        const knownPlayerNames = [
          'Lionel Messi', 'Cristiano Ronaldo', 'Pele', 'Diego Maradona',
          'Zinedine Zidane', 'Johan Cruyff'
        ];
        
        const usingMockData = results.some(player => 
          knownPlayerNames.includes(player.name) && 
          (!player.imageUrl || player.imageUrl.includes('data:image/png;base64'))
        );
        
        setUsingLocalData(usingMockData);
        
        // Log API vs Mock data usage
        if (usingMockData) {
          console.log('📝 Results include mock data - likely fallback or combined results');
        } else {
          console.log('🌐 Results appear to be from live API data');
        }
      }
      
      if (results.length === 0) {
        setErrorMessage('No players found. Try different search criteria.');
      }
    } catch (error) {
      console.error('❌ Error searching for players:', error);
      setErrorMessage('Network issue accessing the player database. Using local player data instead.');
      setUsingLocalData(true);
    } finally {
      setIsSearching(false);
      searchTimeoutRef.current = null;
    }
  };

  const handleSelectPlayer = async (apiPlayer: ApiPlayer) => {
    if (!selectedPosition) {
      dispatch(addNotification({
        message: 'Please select a position on the field first',
        type: 'error'
      }));
      return;
    }

    try {
      // Get player image
      const imageUrl = await getPlayerImage(apiPlayer.id, apiPlayer.name);
      
      const player: Player = {
        id: apiPlayer.id,
        name: apiPlayer.name,
        position: selectedPosition,
        imageUrl: apiPlayer.imageUrl || imageUrl,
        country: apiPlayer.country,
        year: apiPlayer.year,
        team: apiPlayer.team,
        isSubstitute: false,
      };
      
      dispatch(addPlayer(player));
      // Reset only the search query but keep other filters
      setFilters(prev => ({ ...prev, query: '' }));
      setSearchResults([]);
    } catch (error) {
      console.error('Error adding player:', error);
      dispatch(addNotification({
        message: 'Failed to add player. Please try again.',
        type: 'error'
      }));
    }
  };

  const clearFilters = () => {
    setFilters({
      query: '',
      position: '',
      year: '',
      country: '',
      league: '',
      team: '', 
    });
  };

  const positionOptions: Position[] = [
    'GK', 'LB', 'CB', 'RB', 'LWB', 'RWB', 
    'CDM', 'CM', 'CAM', 'LM', 'RM', 
    'LW', 'RW', 'CF', 'ST'
  ];

  // Year options - from 1975 to current year
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: currentYear - 1975 + 1 }, (_, i) => currentYear - i);

  return (
    <div className="player-search">
      <h3>Search Players</h3>
      
      {selectedPosition && (
        <div className="position-hint">
          Adding for position: <span className="position-badge-small">{selectedPosition}</span>
        </div>
      )}
      
      {/* Active Filters Display */}
      {(filters.country || filters.league || filters.team || filters.year) && (
        <div className="active-filters">
          <span className="active-filters-label">Active filters: </span>
          {filters.country && (
            <span className="active-filter-tag">
              Country: {countries.find(c => c.id === filters.country)?.name}
            </span>
          )}
          {filters.league && (
            <span className="active-filter-tag">
              League: {availableLeagues.find(l => l.id === filters.league)?.name}
            </span>
          )}
          {filters.team && (
            <span className="active-filter-tag">
              Team: {availableTeams.find(t => t.id === filters.team)?.name}
            </span>
          )}
          {filters.year && (
            <span className="active-filter-tag">
              Year: {filters.year}
            </span>
          )}
          <button 
            onClick={clearFilters}
            className="clear-filters-small"
            title="Clear all filters"
          >
            ✕ Clear
          </button>
        </div>
      )}
      
      <div className="search-form">
        <div className="search-input-container">
          <input
            ref={searchInputRef}
            type="text"
            value={filters.query}
            onChange={(e) => handleFilterChange('query', e.target.value)}
            placeholder="Search by player name..."
            className="search-input"
          />
          
          <button 
            onClick={handleSearch} 
            disabled={isSearching}
            className="search-button"
          >
            {isSearching ? 'Searching...' : 'Search'}
          </button>
        </div>
        
        {/* Advanced Search Toggle */}
        <button 
          onClick={() => setShowAdvancedSearch(!showAdvancedSearch)}
          className="advanced-search-toggle"
        >
          {showAdvancedSearch ? '← Hide' : '🔍 Advanced'} Search
        </button>
        
        {/* Advanced Search Filters */}
        {showAdvancedSearch && (
          <div className="advanced-search-container">
            <div className="smart-filter-info">
              <span className="info-icon">💡</span>
              <span className="info-text">
                <strong>Smart Filtering:</strong> When you select only a country, you'll see players from that country. 
                When you add league/team filters, you'll see all players from those leagues/teams regardless of nationality 
                (e.g., Mbappé from France playing in Spanish La Liga).
              </span>
            </div>
            
            <div className="advanced-search-row">
              <div className="advanced-search-field">
                <label htmlFor="position-filter">Position:</label>
                <select
                  id="position-filter"
                  value={filters.position}
                  onChange={(e) => handleFilterChange('position', e.target.value)}
                >
                  <option value="">Any Position</option>
                  {positionOptions.map(pos => (
                    <option key={pos} value={pos}>{pos}</option>
                  ))}
                </select>
              </div>
              
              <div className="advanced-search-field">
                <label htmlFor="country-filter">Country:</label>
                <select
                  id="country-filter"
                  value={filters.country}
                  onChange={(e) => handleFilterChange('country', e.target.value)}
                >
                  <option value="">Any Country</option>
                  {countries.map(country => (
                    <option key={country.id} value={country.id}>{country.name}</option>
                  ))}
                </select>
              </div>
            </div>
            
            <div className="advanced-search-row">
              <div className="advanced-search-field">
                <label htmlFor="league-filter">League:</label>
                <select
                  id="league-filter"
                  value={filters.league}
                  onChange={(e) => handleFilterChange('league', e.target.value)}
                  disabled={!filters.country}
                >
                  <option value="">
                    {filters.country ? 'Any League' : 'Select Country First'}
                  </option>
                  {availableLeagues.map(league => (
                    <option key={league.id} value={league.id}>{league.name}</option>
                  ))}
                </select>
              </div>
              
              <div className="advanced-search-field">
                <label htmlFor="team-filter">Team:</label>
                <select
                  id="team-filter"
                  value={filters.team}
                  onChange={(e) => handleFilterChange('team', e.target.value)}
                  disabled={!filters.country || !filters.league}
                >
                  <option value="">
                    {filters.country && filters.league ? 'Any Team' : 'Select League First'}
                  </option>
                  {availableTeams.map(team => (
                    <option key={team.id} value={team.id}>{team.name}</option>
                  ))}
                </select>
              </div>
            </div>
            
            <div className="advanced-search-row">
              <div className="advanced-search-field">
                <label htmlFor="year-filter">Year/Era:</label>
                <select
                  id="year-filter"
                  value={filters.year}
                  onChange={(e) => handleFilterChange('year', e.target.value)}
                >
                  <option value="">Any Year</option>
                  {yearOptions.map(year => (
                    <option key={year} value={year.toString()}>{year}</option>
                  ))}
                </select>
              </div>
              
              <div className="filter-actions">
                <button 
                  onClick={handleSearch} 
                  disabled={isSearching}
                  className="search-button-small"
                >
                  {isSearching ? 'Searching...' : '🔍 Search'}
                </button>
                <button 
                  onClick={clearFilters}
                  className="clear-filters-button"
                >
                  Clear All Filters
                </button>
              </div>
            </div>
          </div>
        )}

        {errorMessage && <div className="error-message">{errorMessage}</div>}
        {usingLocalData && !errorMessage && (
          <div className="info-message">
            Using offline player database. Network connection to online database unavailable.
          </div>
        )}
      </div>
      
      <div className="search-results">
        {searchResults.length > 0 ? (
          searchResults.map((player) => (
            <div
              key={player.id}
              className="player-card"
              onClick={() => handleSelectPlayer(player)}
            >
              <img 
                src={player.imageUrl} 
                alt={player.name} 
                className="player-thumbnail"
                onError={(e) => {
                  const canvas = document.createElement('canvas');
                  canvas.width = 50;
                  canvas.height = 50;
                  const ctx = canvas.getContext('2d');
                  if (ctx) {
                    // Create better silhouette background
                    ctx.fillStyle = '#4CAF50';
                    ctx.beginPath();
                    ctx.arc(25, 25, 25, 0, 2 * Math.PI);
                    ctx.fill();
                    
                    // Draw player silhouette
                    ctx.fillStyle = 'rgba(255,255,255,0.7)';
                    // Head
                    ctx.beginPath();
                    ctx.arc(25, 15, 8, 0, 2 * Math.PI);
                    ctx.fill();
                    
                    // Body
                    ctx.beginPath();
                    ctx.moveTo(17, 25);
                    ctx.lineTo(33, 25);
                    ctx.lineTo(35, 42);
                    ctx.lineTo(15, 42);
                    ctx.closePath();
                    ctx.fill();
                    
                    // Set as image source
                    (e.target as HTMLImageElement).src = canvas.toDataURL();
                  }
                }}
              />
              <div className="player-card-info">
                <div className="player-card-name">{player.name}</div>
                <div className="player-card-details">
                  <span className="position-badge">{player.position}</span>
                  {player.country && <span className="country-badge">{player.country}</span>}
                  {player.year && <span className="year-badge">{player.year}</span>}
                  {player.team && <span className="team-badge">{player.team}</span>}
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="no-results">
            {isSearching ? 'Searching...' : 'No players found'}
          </div>
        )}
      </div>
    </div>
  );
});

export default PlayerSearch; 